/* eslint-disable @typescript-eslint/no-explicit-any */
import Chevron from "../../components/Icons/Chevron";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import Button from "../../components/Shared/Button";
import { useParams } from "react-router-dom";
import { ChaptersDTO, getChapters } from "../../api/Chapters/GetAllChapters";
import Input from "../../components/Shared/Input";
import { ButtonTypes } from "../../utils/consts";
import ChooseLecture from "../../components/Dashboard/AddQuiz/ChooseLecture";
import TextArea from "../../components/Shared/TextArea";
import Spinner from "../../components/Shared/Spinner";
import { toast } from "react-toastify";
import Upload from "../../components/Shared/Upload";
import ChooseOpenLecture from "../../components/Dashboard/AddQuiz/ChooseOpenLecture";
import { CreateIFRS } from "../../api/Quiz/CreateIFRS";
import { IFRSDTO } from "../../api/Quiz/CreateIFRS";

interface Question {
    id: number;
    name: string;
    requirements: string;
    exhibits: {
        title: string;
        type: string;
        filePath: string;
    }[];
    responseOptions: {
        wordProcessor: boolean;
        spreadsheet: boolean;
        spreadsheetFilePath?: string;
    };
}

const AddIFRSQuiz = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [selected, setSelected] = useState<ChaptersDTO | null>(null);
    const [selectedOpenLecture, setSelectedOpenLecture] = useState<ChaptersDTO | null>(null);
    const { id } = useParams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [, setErrors] = useState({
        title: '',
        questions: [] as string[],
    });



    const [essayQuestions, setEssayQuestions] = useState<Question[]>([
        {
            id: 1,
            name: "",
            requirements: "",
            exhibits: [],
            responseOptions: {
                wordProcessor: true,
                spreadsheet: false,
                spreadsheetFilePath: ""
            }
        }
    ]);

    const [nextEssayId, setNextEssayId] = useState(2);

    const [answer, setAnswer] = useState("");

    const validateForm = () => {
        let hasError = false;
        const questionErrors: string[] = [];

        // Validate quiz title
        if (!quizTitle.trim()) {
            setErrors(prev => ({ ...prev, title: "Quiz title is required." }));
            hasError = true;
        } else {
            setErrors(prev => ({ ...prev, title: "" }));
        }
        essayQuestions.forEach((question, index) => {
            if (!question.name.trim()) {
                questionErrors[index] = "Question name is required.";
                hasError = true;
            } else {
                questionErrors[index] = "";
            }
        });

        setErrors(prev => ({ ...prev, questions: questionErrors }));
        return !hasError;
    };

    const chapterId = id || "";
    const { data: ChaptersResponse, isLoading: isChaptersLoading } = useQuery({
        queryFn: () => getChapters(chapterId),
        queryKey: ["chapters", chapterId],
        staleTime: 0,
        refetchInterval: 0,
    });


    const chapters = (ChaptersResponse?.data as ChaptersDTO[] | undefined) || [];
    const [quizTitle, setQuizTitle] = useState("");
    const [quizDuration, setQuizDuration] = useState("");

    const handleAddQuestion = () => {
            const newQuestion: Question = {
                id: nextEssayId,
                name: "",
                requirements: "",
                exhibits: [],
                responseOptions: {
                    wordProcessor: true,
                    spreadsheet: false,
                    spreadsheetFilePath: ""
                }
            };
            setEssayQuestions([...essayQuestions, newQuestion]);
            setNextEssayId(nextEssayId + 1);
    };

    const handleDeleteQuestion = (questionId: number) => {
            setEssayQuestions(essayQuestions.filter((question) => question.id !== questionId));
    };

    const handleQuestionFieldChange = (
        questionIndex: number,
        field: string,
        value: string | boolean | { [key: string]: any }
    ) => {
        setEssayQuestions((prevQuestions) =>
            prevQuestions.map((question, index) => {
                if (index === questionIndex) {
                    return { ...question, [field]: value };
                }
                return question;
            })
        );
    };

    const handleExhibitChange = (
        questionIndex: number,
        exhibitIndex: number,
        field: string,
        value: string
    ) => {
        setEssayQuestions((prevQuestions) =>
            prevQuestions.map((question, qIndex) => {
                if (qIndex === questionIndex) {
                    const updatedExhibits = [...question.exhibits];
                    updatedExhibits[exhibitIndex] = {
                        ...updatedExhibits[exhibitIndex],
                        [field]: value
                    };
                    return { ...question, exhibits: updatedExhibits };
                }
                return question;
            })
        );
    };

    const handleAddExhibit = (questionIndex: number) => {
        setEssayQuestions((prevQuestions) =>
            prevQuestions.map((question, qIndex) => {
                if (qIndex === questionIndex) {
                    return {
                        ...question,
                        exhibits: [
                            ...question.exhibits,
                            { title: "", type: "pdf", filePath: "" }
                        ]
                    };
                }
                return question;
            })
        );
    };

    const handleRemoveExhibit = (questionIndex: number, exhibitIndex: number) => {
        setEssayQuestions((prevQuestions) =>
            prevQuestions.map((question, qIndex) => {
                if (qIndex === questionIndex) {
                    const updatedExhibits = question.exhibits.filter(
                        (_, index) => index !== exhibitIndex
                    );
                    return { ...question, exhibits: updatedExhibits };
                }
                return question;
            })
        );
    };

    const { mutate } = useMutation({
        mutationFn: CreateIFRS,
        onSuccess: () => {
            setIsSubmitting(true);
            toast.success("Quiz Added successfully");
        },
        onError: () => {
            setIsSubmitting(false);
        },
    });

    const handleAnswerUpload = (url: string) => {
        setAnswer(url);
    };

    const handleSubmit = (event: { preventDefault: () => void }) => {
        event.preventDefault();
        if (!selected || !selectedOpenLecture) {
            toast.error("Please select both lectures");
            return;
        }
        if (validateForm()) {
            const durationInSeconds = String(Number(quizDuration) * 60);
            const payload: IFRSDTO = {
                title: quizTitle,
                duration: durationInSeconds,
                answer: answer,
                lectureId: selected?.lectureId || "",
                OpenLectureId: selectedOpenLecture.OpenLectureId || "",
                chapterId: selected?.chapterId || "",
                ifrsQuestions: essayQuestions.map(question => ({
                    text: question.name,
                    exhibits: question.exhibits,
                    requirements: question.requirements,
                    responseOptions: question.responseOptions
                }))
            };

            setIsSubmitting(true);
            mutate(payload);
        }
    };


    return (
        <div className="py-7">
            <h1 className="text-burntBlack font-bold text-[32px] mb-6">
                Create new quiz
            </h1>
            <form
                onSubmit={handleSubmit}
                className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] space-y-5"
            >
                <div className="flex gap-5 items-center mb-9 mt-4 justify-center">
                    <div className={`flex items-center gap-2.5`}>
                        <span
                            className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 1 ? "text-white bg-[#4379EE]" : "text-lightGray"
                                }`}
                        >
                            1
                        </span>
                        <h1 className="font-semibold">Choose Lecture</h1>
                    </div>
                    <Chevron height={20} width={20} />

                    <div className={`flex items-center gap-2.5`}>
                        <span
                            className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 2 ? "text-white bg-[#4379EE]" : "text-lightGray"
                                }`}
                        >
                            2
                        </span>
                        <h1 className="font-semibold">Closed Lecture</h1>
                    </div>
                    <Chevron height={20} width={20} />

                    <div className={`flex items-center gap-2.5 `}>
                        <span
                            className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 3 ? "text-white bg-[#4379EE]" : "text-lightGray"
                                }`}
                        >
                            3
                        </span>
                        <h1 className="font-semibold">Questions</h1>
                    </div>
                </div>
                {currentStep === 1 && (
                    <>
                        {isChaptersLoading ? (
                            <div className="min-h-[50vh] flex justify-center items-center">
                                <Spinner />
                            </div>
                        ) : (
                            <ChooseLecture
                                message="Select a lesson to make quizzes of it"
                                currentStep={currentStep}
                                setCurrentStep={setCurrentStep}
                                selected={selected}
                                setSelected={setSelected}
                                data={chapters}
                            />
                        )}
                    </>
                )}
                {currentStep === 2 && (
                    <>
                        {isChaptersLoading ? (
                            <div className="min-h-[50vh] flex justify-center items-center">
                                <Spinner />
                            </div>
                        ) : (
                            <ChooseOpenLecture
                                message="Select a lesson that closes before successing the quiz"
                                currentStep={currentStep}
                                setCurrentStep={setCurrentStep}
                                selected={selectedOpenLecture}
                                setSelected={setSelectedOpenLecture}
                                data={chapters}
                            />
                        )}
                    </>
                )}
                {currentStep === 3 && (
                    <>
                        <div className="w-full max-w-[544px] mx-auto">
                            <Input
                                className="text-lg font-semibold mb-10"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                title="Quiz Title"
                                placeholder=""
                                value={quizTitle}
                                name="title"
                                onChange={(e) => setQuizTitle(e.target.value)}
                            />
                            <Input
                                className="text-lg font-semibold mb-10"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                title="Quiz Duration (in minutes)"
                                placeholder=""
                                value={quizDuration}
                                name="duration"
                                onChange={(e) => setQuizDuration(e.target.value)}
                            />

                            <Upload
                                acceptedFiles={{
                                    "pdf": [],
                                    "xlsx": [],
                                    "docx": [],
                                }}
                                folder="quiz_answers"
                                onFileUploaded={handleAnswerUpload}
                            >
                                <Input
                                    upload={true}
                                    className="text-lg font-semibold"
                                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                    placeholder="Upload answer file"
                                    title="Quiz Answer"
                                    value={answer}
                                    name="answer"
                                />
                            </Upload>

                            <div className="mt-10">
                                <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
                                <p className="text-lightGray font-medium">
                                    Nice text goes here to explain the question creation part like
                                    never before
                                </p>
                            </div>
                            <div>
                                {essayQuestions.map((question, questionIndex) => (
                                    <div key={question.id}>
                                        <div className="mt-9">
                                            <Input
                                                className="text-lg font-semibold"
                                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                                                title="Question Text"
                                                placeholder=""
                                                name="name"
                                                value={question.name}
                                                onChange={(e) =>
                                                    handleQuestionFieldChange(
                                                        questionIndex,
                                                        "name",
                                                        e.target.value,
                                                    )
                                                }
                                                type="text"
                                            />
                                            <TextArea
                                                className="text-lg font-semibold mb-10"
                                                inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                                                title="Requirements"
                                                placeholder=""
                                                name="requirements"
                                                value={question.requirements}
                                                onChange={(e) =>
                                                    handleQuestionFieldChange(
                                                        questionIndex,
                                                        "requirements",
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            
                                            <div className="mb-10">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-lg font-semibold">Exhibits</h3>
                                                    <Button
                                                        onClick={() => handleAddExhibit(questionIndex)}
                                                        type={ButtonTypes.BUTTON}
                                                        variant="transparent"
                                                        className="!h-8 !px-4 !text-sm !font-bold border border-primary text-primary"
                                                    >
                                                        Add Exhibit
                                                    </Button>
                                                </div>
                                                
                                                {question.exhibits.map((exhibit, exhibitIndex) => (
                                                    <div key={exhibitIndex} className="mb-4 p-4 border border-black/15 rounded-lg">
                                                        <div className="flex justify-between items-start mb-4">
                                                            <h4 className="text-md font-medium">Exhibit {exhibitIndex + 1}</h4>
                                                            <button
                                                                onClick={() => handleRemoveExhibit(questionIndex, exhibitIndex)}
                                                                className="text-red-500 hover:text-red-700"
                                                                type="button"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                        
                                                        <Input
                                                            className="text-lg font-semibold mb-4"
                                                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                                            title="Title"
                                                            placeholder="Enter exhibit title"
                                                            value={exhibit.title}
                                                            onChange={(e) =>
                                                                handleExhibitChange(
                                                                    questionIndex,
                                                                    exhibitIndex,
                                                                    "title",
                                                                    e.target.value
                                                                )
                                                            }
                                                            name="title"
                                                        />
                                                        
                                                        <div className="mb-4">
                                                            <label className="block text-sm font-medium mb-2">Type</label>
                                                            <select
                                                                className="w-full bg-[#F5F6F8] h-12 border border-black/15 rounded px-3"
                                                                value={exhibit.type}
                                                                onChange={(e) =>
                                                                    handleExhibitChange(
                                                                        questionIndex,
                                                                        exhibitIndex,
                                                                        "type",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            >
                                                                <option value="pdf">PDF</option>
                                                                <option value="excel">Excel</option>
                                                                <option value="word">Word</option>
                                                                <option value="image">Image</option>
                                                            </select>
                                                        </div>
                                                        
                                                        <Upload
                                                            acceptedFiles={{
                                                                "pdf": [],
                                                                "xlsx": [],
                                                                "docx": [],
                                                                "png": [],
                                                                "jpg": [],
                                                                "jpeg": []
                                                            }}
                                                            folder="quiz_exhibits"
                                                            onFileUploaded={(url) =>
                                                                handleExhibitChange(
                                                                    questionIndex,
                                                                    exhibitIndex,
                                                                    "filePath",
                                                                    url
                                                                )
                                                            }
                                                        >
                                                            <Input
                                                                upload={true}
                                                                className="text-lg font-semibold"
                                                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                                                placeholder="Upload file"
                                                                title="File"
                                                                value={exhibit.filePath}
                                                                name="filePath"
                                                            />
                                                        </Upload>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="mb-10">
                                                <h3 className="text-lg font-semibold mb-4">Response Options</h3>
                                                <div className="space-y-4">
                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={question.responseOptions.wordProcessor}
                                                            onChange={(e) =>
                                                                handleQuestionFieldChange(
                                                                    questionIndex,
                                                                    "responseOptions",
                                                                    {
                                                                        ...question.responseOptions,
                                                                        wordProcessor: e.target.checked
                                                                    }
                                                                )
                                                            }
                                                            className="w-4 h-4 rounded border-gray-300"
                                                        />
                                                        <span className="text-sm font-medium">Enable Word Processor</span>
                                                    </label>

                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={question.responseOptions.spreadsheet}
                                                            onChange={(e) =>
                                                                handleQuestionFieldChange(
                                                                    questionIndex,
                                                                    "responseOptions",
                                                                    {
                                                                        ...question.responseOptions,
                                                                        spreadsheet: e.target.checked
                                                                    }
                                                                )
                                                            }
                                                            className="w-4 h-4 rounded border-gray-300"
                                                        />
                                                        <span className="text-sm font-medium">Enable Spreadsheet</span>
                                                    </label>

                                                    {question.responseOptions.spreadsheet && (
                                                        <Upload
                                                            acceptedFiles={{
                                                                "xlsx": [],
                                                                "xls": [],
                                                                "csv": []
                                                            }}
                                                            folder="quiz_spreadsheets"
                                                            onFileUploaded={(url) =>
                                                                handleQuestionFieldChange(
                                                                    questionIndex,
                                                                    "responseOptions",
                                                                    {
                                                                        ...question.responseOptions,
                                                                        spreadsheetFilePath: url
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <Input
                                                                upload={true}
                                                                className="text-lg font-semibold"
                                                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                                                placeholder="Upload spreadsheet template"
                                                                title="Spreadsheet Template"
                                                                value={question.responseOptions.spreadsheetFilePath}
                                                                name="spreadsheetFilePath"
                                                            />
                                                        </Upload>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-5 mt-10">
                                            <Button
                                                onClick={() => handleDeleteQuestion(question.id)}
                                                className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary ${essayQuestions.length === 1
                                                    ? "opacity-50 cursor-not-allowed"
                                                    : ""
                                                    }`}
                                                disabled={essayQuestions.length === 1}
                                                type={ButtonTypes.BUTTON}
                                                variant="transparent"
                                            >
                                                Delete
                                            </Button>
                                            <Button
                                                onClick={() => handleAddQuestion()}
                                                type={ButtonTypes.BUTTON}
                                                variant="primary"
                                                className="!h-12 w-40 !text-base !font-bold "
                                            >
                                                Add New Question
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-between w-full">
                            <Button
                                disabled={!selected}
                                onClick={() => setCurrentStep(2)}
                                className={`mt-8 !bg-white border-black/20 border !text-burntBlack w-[160px] !h-11 !text-base font-bold ${!selected ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                                variant="primary"
                            >
                                Cancel
                            </Button>
                            <Button
                                type={ButtonTypes.SUBMIT}
                                disabled={isSubmitting}
                                className={`mt-8 w-[160px] !h-11 !text-base font-bold ${!selected ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                                variant="primary"
                            >
                                Send Replies
                            </Button>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

export default AddIFRSQuiz;
